import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Save, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const FIELD = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-900";

/** Admin-only AI Configuration. Provider/model/limits are configurable; the API key itself is
 *  never entered or shown here — only the name of the environment variable that holds it. */
export function AiConfigPanel({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ providers: [], provider_models: {}, key_configured: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ai/config").then(({ data }) => {
      setCfg(data.config);
      setMeta({ providers: data.providers, provider_models: data.provider_models,
                key_configured: data.key_configured });
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ai/config", cfg);
      setCfg(data.config);
      setMeta((m) => ({ ...m, key_configured: data.key_configured }));
      toast.success("AI configuration saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const models = (p) => meta.provider_models?.[p] || [];

  // Rendered through a portal to document.body so it escapes the AI page's fixed stacking
  // context and always sits above the app header.
  return createPortal(
    <div className="fixed inset-0 z-[200] flex justify-end bg-slate-900/40" data-testid="ai-config-overlay">
      <button className="flex-1" aria-label="Close" onClick={onClose} />
      <div className="h-full w-full max-w-md overflow-y-auto bg-white p-6 shadow-2xl" data-testid="ai-config-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#0F172A] text-white">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div>
              <p className="font-ui text-sm font-bold text-slate-900">AI Configuration</p>
              <p className="text-[11px] text-slate-400">Provider-agnostic · admin only</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="ai-config-close" className="rounded-md p-1 hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        {!cfg ? (
          <div className="mt-10 grid place-items-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="mt-6 space-y-5">
            <div className={`rounded-lg border p-3 text-xs ${meta.key_configured ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}
              data-testid="ai-key-status">
              {meta.key_configured
                ? "API key detected in the server environment. Keys are never shown here."
                : "No API key found for the selected environment variable. Add it in server Secrets."}
            </div>

            <label className="block">
              <span className="form-label mb-1 block text-xs font-semibold">Provider</span>
              <select className={FIELD} value={cfg.provider} data-testid="ai-cfg-provider"
                onChange={(e) => { const p = e.target.value; setCfg((c) => ({ ...c, provider: p, model: models(p)[0] || c.model })); }}>
                {meta.providers.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>

            <label className="block">
              <span className="form-label mb-1 block text-xs font-semibold">Model</span>
              <input list="ai-model-list" className={FIELD} value={cfg.model} data-testid="ai-cfg-model"
                onChange={(e) => set("model", e.target.value)} />
              <datalist id="ai-model-list">{models(cfg.provider).map((m) => <option key={m} value={m} />)}</datalist>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="form-label mb-1 block text-xs font-semibold">Temperature</span>
                <input type="number" step="0.1" min="0" max="2" className={FIELD} value={cfg.temperature}
                  data-testid="ai-cfg-temperature" onChange={(e) => set("temperature", parseFloat(e.target.value))} />
              </label>
              <label className="block">
                <span className="form-label mb-1 block text-xs font-semibold">Max output tokens</span>
                <input type="number" min="128" max="8192" className={FIELD} value={cfg.max_output_tokens}
                  data-testid="ai-cfg-maxtokens" onChange={(e) => set("max_output_tokens", parseInt(e.target.value, 10))} />
              </label>
            </div>

            <label className="block">
              <span className="form-label mb-1 block text-xs font-semibold">Context window (recent turns)</span>
              <input type="number" min="0" max="20" className={FIELD} value={cfg.history_window}
                data-testid="ai-cfg-history" onChange={(e) => set("history_window", parseInt(e.target.value, 10))} />
            </label>

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="mb-3 text-xs font-semibold text-slate-500">Fallback (used if the primary model errors)</p>
              <div className="grid grid-cols-2 gap-3">
                <select className={FIELD} value={cfg.fallback_provider || ""} data-testid="ai-cfg-fallback-provider"
                  onChange={(e) => { const p = e.target.value; setCfg((c) => ({ ...c, fallback_provider: p, fallback_model: models(p)[0] || c.fallback_model })); }}>
                  <option value="">None</option>
                  {meta.providers.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input list="ai-model-list" className={FIELD} value={cfg.fallback_model || ""}
                  placeholder="model" data-testid="ai-cfg-fallback-model"
                  onChange={(e) => set("fallback_model", e.target.value)} />
              </div>
            </div>

            <label className="block">
              <span className="form-label mb-1 block text-xs font-semibold">API key environment variable</span>
              <input className={FIELD} value={cfg.api_key_env} data-testid="ai-cfg-keyenv"
                onChange={(e) => set("api_key_env", e.target.value)} />
              <span className="mt-1 block text-[11px] text-slate-400">Name of the server env/secret that holds the key (e.g. EMERGENT_LLM_KEY). The value is never stored or shown.</span>
            </label>

            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!cfg.enabled} data-testid="ai-cfg-enabled"
                onChange={(e) => set("enabled", e.target.checked)} />
              <span className="text-sm text-slate-700">AI copilot enabled for this business</span>
            </label>

            <button onClick={save} disabled={saving} data-testid="ai-cfg-save"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] px-6 py-3 text-sm font-medium text-white hover:bg-[#1E293B] disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save configuration
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
